import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const ThemeModeContext = createContext();

export const useThemeMode = () => useContext(ThemeModeContext);

const STORAGE_KEY = "themeMode";

const getSystemMode = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

// Brand colors are set per-project via REACT_APP_COLOR_* env vars (see the
// *_DARKMODE variants below), overriding the defaults in colors.module.css
// at runtime - kept here (rather than a static index.js script) so it can
// re-run whenever the theme mode changes.
const BRAND_COLOR_VARS = [
  {
    cssVar: "--color-primary",
    light: "REACT_APP_COLOR_PRIMARY",
    dark: "REACT_APP_COLOR_PRIMARY_DARKMODE",
  },
  {
    cssVar: "--color-primary-dark",
    light: "REACT_APP_COLOR_PRIMARY_DARK",
    dark: "REACT_APP_COLOR_PRIMARY_DARK_DARKMODE",
  },
  {
    cssVar: "--color-primary-light",
    light: "REACT_APP_COLOR_PRIMARY_LIGHT",
    dark: "REACT_APP_COLOR_PRIMARY_LIGHT_DARKMODE",
  },
];

const applyBrandColors = (mode) => {
  BRAND_COLOR_VARS.forEach(({ cssVar, light, dark }) => {
    const value =
      (mode === "dark" && process.env[dark]) || process.env[light];
    if (value) {
      document.documentElement.style.setProperty(cssVar, value);
    }
  });
};

export const ThemeModeProvider = ({ children }) => {
  const [explicitMode, setExplicitMode] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null
  );
  const [systemMode, setSystemMode] = useState(getSystemMode);
  // Lets a specific page (e.g. the login screen) force light mode without
  // touching the user's actual saved/system preference underneath it.
  const [forceLight, setForceLight] = useState(false);

  const preferredMode = explicitMode || systemMode;
  const effectiveMode = forceLight ? "light" : preferredMode;

  useEffect(() => {
    if (explicitMode) return undefined;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => setSystemMode(mql.matches ? "dark" : "light");
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, [explicitMode]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", effectiveMode);
    applyBrandColors(effectiveMode);
  }, [effectiveMode]);

  const toggleMode = () => {
    const next = preferredMode === "dark" ? "light" : "dark";
    setExplicitMode(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: effectiveMode,
          background: {
            default: "var(--color-bg)",
            paper: "var(--color-bg-white)",
          },
        },
      }),
    [effectiveMode]
  );

  return (
    <ThemeModeContext.Provider
      value={{ mode: preferredMode, toggleMode, setForceLight }}
    >
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
